import merge from 'lodash/merge';
import type { ReactNode } from 'react';
import React, { createContext, useContext, useMemo, useRef } from 'react';
import type { DomainSpecificInfoType } from '../models';

/**
 * Logger context data interface containing tracking metadata.
 * These values are stored in a ref and read lazily to prevent re-renders.
 */
export interface LoggerDataContextType {
  activeTab?: string;
  activeView?: string;
  domainSpecificInfo?: DomainSpecificInfoType;
  feature?: string;
  featureLocal?: string;
  section?: string;
  sectionLocal?: string;
}

/**
 * Internal context value structure using ref-based approach.
 * Contains stable getter function to prevent consumer re-renders.
 * @internal
 */
interface LoggerDataContextValue {
  /** Returns current context values from ref */
  getValues: () => LoggerDataContextType;
}

export const defaultLoggerDataContextValue: LoggerDataContextType = {
  feature: undefined,
  featureLocal: undefined,
  activeTab: undefined,
  activeView: undefined,
  section: undefined,
  sectionLocal: undefined,
  domainSpecificInfo: undefined,
};

const defaultContextValue: LoggerDataContextValue = {
  getValues: () => defaultLoggerDataContextValue,
};
/**
 * Holds the context for the logger data.
 * Uses a ref-based approach to prevent re-renders of consuming components.
 * @tutorial wiki: https://webster.bfm.com/Wiki/display/AFWM/AW+Event+Logging+%28Web%29+-+Developer+Guide#AWEventLogging(Web)DeveloperGuide-Settinguploggercontextdata
 */
export const LoggerDataContext = (() => {
  const context = createContext<LoggerDataContextValue>(defaultContextValue);
  context.displayName = 'LoggerDataContext';
  return context;
})();

export const LoggerDataContextProvider: React.FC<
  LoggerDataContextType & {
    children: ReactNode;
  }
> = ({ children, feature, featureLocal, section, sectionLocal, activeTab, activeView, domainSpecificInfo }) => {
  const parentContext = useContext(LoggerDataContext);
  const ownPropsRef = useRef<LoggerDataContextType>({});

  // eslint-disable-next-line react-compiler/react-compiler -- intentional ref mutation for lazy context reads
  ownPropsRef.current = {
    feature,
    featureLocal,
    section,
    sectionLocal,
    activeTab,
    activeView,
    domainSpecificInfo,
  };

  // stable context value to prevent re-renders
  const contextValue = useMemo<LoggerDataContextValue>(
    () => ({
      getValues: () => {
        const parentValues = parentContext.getValues();
        const ownProps = ownPropsRef.current;
        return {
          ...parentValues,
          feature: ownProps.feature ?? parentValues.feature,
          featureLocal: ownProps.featureLocal ?? parentValues.featureLocal,
          section: ownProps.section ?? parentValues.section,
          sectionLocal: ownProps.sectionLocal ?? parentValues.sectionLocal,
          activeTab: ownProps.activeTab ?? parentValues.activeTab,
          activeView: ownProps.activeView ?? parentValues.activeView,
          domainSpecificInfo: ownProps.domainSpecificInfo
            ? merge({}, parentValues.domainSpecificInfo, ownProps.domainSpecificInfo)
            : parentValues.domainSpecificInfo,
        };
      },
    }),
    // parentContext is stable (useMemo [] in parent), so this dep is safe
    [parentContext]
  );

  return <LoggerDataContext.Provider value={contextValue}>{children}</LoggerDataContext.Provider>;
};
LoggerDataContextProvider.displayName = 'LoggerDataContextProvider';
export const LoggerDataContextConsumer = LoggerDataContext.Consumer;
